import { useState, useEffect, type FormEvent } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Spinner } from '@/components/ui/Spinner'
import { Avatar } from '@/components/ui/Avatar'
import { toast } from '@/components/ui/Toast'
import { requireClient } from '@/lib/matrix/client'

interface InvitePeopleDialogProps {
  roomId: string
  roomName: string
  onClose: () => void
}

interface UserResult {
  userId: string
  displayName?: string
  avatarUrl?: string | null
}

const MXID_RE = /^@[^:]+:.+/

export function InvitePeopleDialog({ roomId, roomName, onClose }: InvitePeopleDialogProps) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<UserResult[]>([])
  const [searching, setSearching] = useState(false)
  const [invitedIds, setInvitedIds] = useState<Set<string>>(new Set())
  const [pendingId, setPendingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const term = query.trim()
    if (term.length < 2) {
      setResults([])
      return
    }
    let cancelled = false
    setSearching(true)
    const handle = setTimeout(async () => {
      try {
        const res = await requireClient().searchUserDirectory({ term, limit: 10 })
        if (cancelled) return
        setResults(
          res.results.map((r) => ({
            userId: r.user_id,
            displayName: r.display_name,
            avatarUrl: r.avatar_url ?? null,
          }))
        )
      } catch {
        if (!cancelled) setResults([])
      } finally {
        if (!cancelled) setSearching(false)
      }
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(handle)
    }
  }, [query])

  const invite = async (userId: string) => {
    if (pendingId || invitedIds.has(userId)) return
    setPendingId(userId)
    setError(null)
    try {
      await requireClient().invite(roomId, userId)
      setInvitedIds((prev) => new Set(prev).add(userId))
      toast(`Invited ${userId}`, 'success')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to invite')
    } finally {
      setPendingId(null)
    }
  }

  // Submitting with a raw @user:server that isn't in the results invites directly.
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    const term = query.trim()
    if (results[0]) invite(results[0].userId)
    else if (MXID_RE.test(term)) invite(term)
    else setError('Enter a full user ID (e.g. @alice:example.org) or pick a result')
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-md bg-[rgb(var(--np-surface))] border border-[rgb(var(--np-border))] rounded-lg p-6 animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-[rgb(var(--np-tx-primary))] mb-1">Invite people</h2>
        <p className="text-xs text-[rgb(var(--np-tx-muted))] mb-4 truncate">to {roomName}</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            autoFocus
            label="Search"
            placeholder="Name or @user:server"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />

          <div className="min-h-[3rem] max-h-64 overflow-y-auto -mx-1">
            {searching && results.length === 0 && (
              <div className="flex items-center justify-center py-4">
                <Spinner size={18} />
              </div>
            )}
            {!searching && query.trim().length >= 2 && results.length === 0 && (
              <p className="text-xs text-[rgb(var(--np-tx-muted))] px-2 py-3">No users found</p>
            )}
            {results.map((user) => {
              const invited = invitedIds.has(user.userId)
              return (
                <button
                  key={user.userId}
                  type="button"
                  onClick={() => invite(user.userId)}
                  disabled={!!pendingId || invited}
                  className="w-full flex items-center gap-3 px-2 py-2 rounded-md text-left hover:bg-[rgb(var(--np-raised))] transition-colors disabled:opacity-50"
                >
                  <Avatar mxc={user.avatarUrl} name={user.displayName || user.userId} userId={user.userId} size={32} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-[rgb(var(--np-tx-primary))] truncate">
                      {user.displayName || user.userId}
                    </div>
                    {user.displayName && (
                      <div className="text-xs text-[rgb(var(--np-tx-muted))] truncate">{user.userId}</div>
                    )}
                  </div>
                  {pendingId === user.userId ? (
                    <Spinner size={14} />
                  ) : invited ? (
                    <span className="text-[11px] text-[rgb(var(--np-status-ok))]">Invited</span>
                  ) : (
                    <span className="text-[11px] text-[rgb(var(--np-accent))]">Invite</span>
                  )}
                </button>
              )
            })}
          </div>

          {error && (
            <div className="border-l-2 border-l-[rgb(var(--np-status-critical))] bg-[rgb(var(--np-status-critical)/0.05)] px-4 py-2 rounded-r">
              <p className="text-xs text-[rgb(var(--np-status-critical))]">{error}</p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" type="button" onClick={onClose}>Done</Button>
          </div>
        </form>
      </div>
    </div>
  )
}
